import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiResponseStatus } from 'models/enums';
import { AdminEmployeeInfo, EmployeeLeaveData, LeaveData, GoalAssignmentData, HRReviewEmployeeGoalAssignment, GoalReviewResponse, GoalReviewsResponse, AddGoalReviewRequest } from 'models/models';
import { request } from 'services/apis';
import { toast } from 'react-toastify';

export interface AdminEmployeeState {
  employeeData: AdminEmployeeInfo[];
  getUsersStatus: ApiResponseStatus;
  getUserError: string | null;
  createUserStatus: ApiResponseStatus;
  createUserError: string | null;
  updateUserStatus: ApiResponseStatus;
  updateUserError: string | null;
  allLeaveSummary: EmployeeLeaveData[];
  allLeaveSummaryStatus: ApiResponseStatus;
  allLeaveSummaryError: string | null;
  goalAssignments: GoalAssignmentData[];
  goalAssignmentsStatus: ApiResponseStatus;
  goalAssignmentsError: string | null;
  supervisorGoalAssignments: GoalAssignmentData[];
  supervisorGoalAssignmentsStatus: ApiResponseStatus;
  supervisorGoalAssignmentsError: string | null;
  employeeGoalAssignments: HRReviewEmployeeGoalAssignment[];
  employeeGoalAssignmentsStatus: ApiResponseStatus;
  employeeGoalAssignmentsError: string | null;
  goalReviews: GoalReviewsResponse;
  goalReviewStatus: ApiResponseStatus;
  goalReviewError: string | null;
  addGoalReviewStatus: ApiResponseStatus;
  addGoalReviewError: string | null;
}

export const createEmployee = createAsyncThunk(
  'admin/createAdminEmployeeCreate',
  async (employeeData: AdminEmployeeInfo) => {
    try {
      const response = await request.post<any>(
        '/employees/create',
        employeeData
      );

      // Check if response includes email status info
      if (response.data.message) {
        // Show appropriate toast based on email status
        if (response.data.emailSent) {
          toast.success(response.data.message);
        } else {
          // Show warning if email failed but employee was created
          toast.warning(response.data.message);
        }
      } else {
        // Fallback for old response format
        toast.success('Employee created successfully!');
      }

      return response.data;
    } catch (error) {
      console.error(error);
      toast.error('Failed to create employee.');
      throw error;
    }
  }
);

export const updateEmployee = createAsyncThunk(
  'admin/updateAdminEmployeeUpdate',
  async (employeeData: AdminEmployeeInfo) => {
    try {
      const response = await request.put<AdminEmployeeInfo>(
        `/employees/update-employeeInfo?userId=${employeeData.userRoleId}`,
        employeeData
      );
      toast.success('Employee info updated successfully!');
      return response.data;
    } catch (error) {
      console.error(error);
      toast.error('Failed to update employee info.');
      throw error;
    }
  }
);

export const getAdminEmployeeInfo = createAsyncThunk(
  'admin/getAdminEmployeeInfo',
  async () => {
    try {
      const response = await request.get<AdminEmployeeInfo[]>(
        '/employees/all-info'
      );

      const activeEmployees = response.data.sort((a, b) =>
        a.epfNumber?.localeCompare(b.epfNumber)
      );
      return activeEmployees;
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch employee info.');
      throw error;
    }
  }
);

export const getAllLeaveSummary = createAsyncThunk(
  'admin/getAllLeaveSummary',
  async (search?: string) => {
    try {
      const searchParam =
        search && search.trim()
          ? `?search=${encodeURIComponent(search.trim())}`
          : '';
      const response = await request.get<EmployeeLeaveData[]>(
        `/Leave/leave-allsummary${searchParam}`
      );
      const activeEmployees = response.data
        .filter(
          (employee) =>
            employee.isActive === true || employee.isActive === 'true'
        )
        .sort((a, b) => a.epfNumber.localeCompare(b.epfNumber));

      return activeEmployees;
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch leave summary info.');
      throw error;
    }
  }
);

export const getLeaveDetails = createAsyncThunk(
  'admin/getLeaveDetails',
  async (email: string) => {
    try {
      const response = await request.get<LeaveData>(
        `/Leave/leave-details/${email}`
      );
      return response.data;
    } catch (error) {
      toast.error('Failed to fetch leave details');
      throw error;
    }
  }
);

export const getGoalAssignments = createAsyncThunk(
  'admin/getGoalAssignments',
  async () => {
    try {
      const response = await request.get<GoalAssignmentData[]>(
        '/GoalAssignment/get'
      );
      return response.data;
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch goal assignments.');
      throw error;
    }
  }
);

export const getSupervisorGoalAssignments = createAsyncThunk(
  'admin/getSupervisorGoalAssignments',
  async (supervisorId: string) => {
    try {
      const response = await request.get<GoalAssignmentData[]>(
        `/GoalAssignment/supervisor/${supervisorId}`
      );
      return response.data;
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch supervisor goal assignments.');
      throw error;
    }
  }
);

export const getEmployeeGoalAssignments = createAsyncThunk(
  'admin/getEmployeeGoalAssignments',
  async (employeeId: string) => {
    try {
      const response = await request.get<HRReviewEmployeeGoalAssignment[]>(
        `/GoalAssignment/employee/${employeeId}`
      );
      return response.data;
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch employee goal assignments.');
      throw error;
    }
  }
);

export const getGoalReview = createAsyncThunk(
  'admin/getGoalReview',
  async (goalAssignmentGoalId: string) => {
    try {
      const response = await request.get<GoalReviewsResponse>(
        `/GoalAssignment/goal-reviews/${goalAssignmentGoalId}`
      );
      return response.data;
    } catch (error: any) {
      console.error(error);
      // Don't show error toast if review is not found (404), as this is expected
      if (error?.response?.status !== 404) {
        toast.error('Failed to fetch goal reviews.');
      }
      throw error;
    }
  }
);

export const addGoalReview = createAsyncThunk(
  'admin/addGoalReview',
  async ({ goalAssignmentGoalId, reviewData }: { goalAssignmentGoalId: string; reviewData: AddGoalReviewRequest }) => {
    try {
      const response = await request.post<GoalReviewResponse>(
        `/GoalAssignment/${goalAssignmentGoalId}/add-goal-review`,
        reviewData
      );
      toast.success('Review added successfully!');
      return response.data;
    } catch (error) {
      console.error(error);
      toast.error('Failed to add review.');
      throw error;
    }
  }
);

const initialState: AdminEmployeeState = {
  employeeData: [],
  getUsersStatus: ApiResponseStatus.idle,
  getUserError: null,
  createUserStatus: ApiResponseStatus.idle,
  createUserError: null,
  updateUserStatus: ApiResponseStatus.idle,
  updateUserError: null,
  allLeaveSummary: [],
  allLeaveSummaryStatus: ApiResponseStatus.idle,
  allLeaveSummaryError: null,
  goalAssignments: [],
  goalAssignmentsStatus: ApiResponseStatus.idle,
  goalAssignmentsError: null,
  supervisorGoalAssignments: [],
  supervisorGoalAssignmentsStatus: ApiResponseStatus.idle,
  supervisorGoalAssignmentsError: null,
  employeeGoalAssignments: [],
  employeeGoalAssignmentsStatus: ApiResponseStatus.idle,
  employeeGoalAssignmentsError: null,
  goalReviews: [],
  goalReviewStatus: ApiResponseStatus.idle,
  goalReviewError: null,
  addGoalReviewStatus: ApiResponseStatus.idle,
  addGoalReviewError: null
};

const adminEmployeeSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Create employee info
    builder
      .addCase(createEmployee.pending, (state) => {
        state.createUserStatus = ApiResponseStatus.pending;
        state.createUserError = null;
      })
      .addCase(createEmployee.fulfilled, (state) => {
        state.createUserStatus = ApiResponseStatus.fulfilled;
        state.createUserError = null;
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.createUserStatus = ApiResponseStatus.rejected;
        state.createUserError = action.error.message ?? 'Unknown error';
      });

    // Get employee info
    builder
      .addCase(getAdminEmployeeInfo.pending, (state) => {
        state.getUsersStatus = ApiResponseStatus.pending;
        state.getUserError = null;
      })
      .addCase(getAdminEmployeeInfo.fulfilled, (state, { payload }) => {
        state.getUsersStatus = ApiResponseStatus.fulfilled;
        state.getUserError = null;
        state.employeeData = payload as AdminEmployeeInfo[];
      })
      .addCase(getAdminEmployeeInfo.rejected, (state, action) => {
        state.getUsersStatus = ApiResponseStatus.rejected;
        state.getUserError = action.error.message ?? 'Unknown error';
      });

    // Update employee info
    builder
      .addCase(updateEmployee.pending, (state) => {
        state.updateUserStatus = ApiResponseStatus.pending;
        state.updateUserError = null;
      })
      .addCase(updateEmployee.fulfilled, (state) => {
        state.updateUserStatus = ApiResponseStatus.fulfilled;
        state.updateUserError = null;
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.updateUserStatus = ApiResponseStatus.rejected;
        state.updateUserError = action.error.message ?? 'Unknown error';
      });

    // Get all leave summary
    builder
      .addCase(getAllLeaveSummary.pending, (state) => {
        state.allLeaveSummaryStatus = ApiResponseStatus.pending;
        state.allLeaveSummaryError = null;
      })
      .addCase(getAllLeaveSummary.fulfilled, (state, { payload }) => {
        state.allLeaveSummaryStatus = ApiResponseStatus.fulfilled;
        state.allLeaveSummaryError = null;
        state.allLeaveSummary = payload as EmployeeLeaveData[];
      })
      .addCase(getAllLeaveSummary.rejected, (state, action) => {
        state.allLeaveSummaryStatus = ApiResponseStatus.rejected;
        state.allLeaveSummaryError = action.error.message ?? 'Unknown error';
      });

    // Get goal assignments
    builder
      .addCase(getGoalAssignments.pending, (state) => {
        state.goalAssignmentsStatus = ApiResponseStatus.pending;
        state.goalAssignmentsError = null;
      })
      .addCase(getGoalAssignments.fulfilled, (state, { payload }) => {
        state.goalAssignmentsStatus = ApiResponseStatus.fulfilled;
        state.goalAssignmentsError = null;
        state.goalAssignments = payload as GoalAssignmentData[];
      })
      .addCase(getGoalAssignments.rejected, (state, action) => {
        state.goalAssignmentsStatus = ApiResponseStatus.rejected;
        state.goalAssignmentsError = action.error.message ?? 'Unknown error';
      });

    builder
      .addCase(getSupervisorGoalAssignments.pending, (state) => {
        state.supervisorGoalAssignmentsStatus = ApiResponseStatus.pending;
        state.supervisorGoalAssignmentsError = null;
      })
      .addCase(getSupervisorGoalAssignments.fulfilled, (state, { payload }) => {
        state.supervisorGoalAssignmentsStatus = ApiResponseStatus.fulfilled;
        state.supervisorGoalAssignmentsError = null;
        state.supervisorGoalAssignments = payload as GoalAssignmentData[];
      })
      .addCase(getSupervisorGoalAssignments.rejected, (state, action) => {
        state.supervisorGoalAssignmentsStatus = ApiResponseStatus.rejected;
        state.supervisorGoalAssignmentsError = action.error.message ?? 'Unknown error';
      });

    // Get employee goal assignments
    builder
      .addCase(getEmployeeGoalAssignments.pending, (state) => {
        state.employeeGoalAssignmentsStatus = ApiResponseStatus.pending;
        state.employeeGoalAssignmentsError = null;
      })
      .addCase(getEmployeeGoalAssignments.fulfilled, (state, { payload }) => {
        state.employeeGoalAssignmentsStatus = ApiResponseStatus.fulfilled;
        state.employeeGoalAssignmentsError = null;
        state.employeeGoalAssignments = payload as HRReviewEmployeeGoalAssignment[];
      })
      .addCase(getEmployeeGoalAssignments.rejected, (state, action) => {
        state.employeeGoalAssignmentsStatus = ApiResponseStatus.rejected;
        state.employeeGoalAssignmentsError = action.error.message ?? 'Unknown error';
      });

    // Get goal reviews
    builder
      .addCase(getGoalReview.pending, (state) => {
        state.goalReviewStatus = ApiResponseStatus.pending;
        state.goalReviewError = null;
        state.goalReviews = []; // Clear previous review data
      })
      .addCase(getGoalReview.fulfilled, (state, { payload }) => {
        state.goalReviewStatus = ApiResponseStatus.fulfilled;
        state.goalReviewError = null;
        state.goalReviews = payload as GoalReviewsResponse;
      })
      .addCase(getGoalReview.rejected, (state, action) => {
        state.goalReviewStatus = ApiResponseStatus.rejected;
        state.goalReviewError = action.error.message ?? 'Unknown error';
        state.goalReviews = []; // Clear review data when not found
      });

    // Add goal review
    builder
      .addCase(addGoalReview.pending, (state) => {
        state.addGoalReviewStatus = ApiResponseStatus.pending;
        state.addGoalReviewError = null;
      })
      .addCase(addGoalReview.fulfilled, (state, { payload }) => {
        state.addGoalReviewStatus = ApiResponseStatus.fulfilled;
        state.addGoalReviewError = null;
        // Add the new review to the existing reviews array
        state.goalReviews.push(payload as GoalReviewResponse);
      })
      .addCase(addGoalReview.rejected, (state, action) => {
        state.addGoalReviewStatus = ApiResponseStatus.rejected;
        state.addGoalReviewError = action.error.message ?? 'Unknown error';
      });

    // Get all leave details
    // builder
    //   .addCase(getLeaveDetails.pending, (state) => {
    //     state.leaveDetailsStatus = 'loading';
    //     state.leaveDetailsError = null;
    //   })
    //   .addCase(
    //     getLeaveDetails.fulfilled,
    //     (state, action: PayloadAction<LeaveDetails>) => {
    //       state.leaveDetailsStatus = 'succeeded';
    //       state.leaveDetails = action.payload;
    //     }
    //   )
    //   .addCase(getLeaveDetails.rejected, (state, action) => {
    //     state.leaveDetailsStatus = 'failed';
    //     state.leaveDetailsError =
    //       action.error.message || 'Failed to fetch leave details';
    //   });
  }
});

const { reducer } = adminEmployeeSlice;

export default reducer;
